import {
  BookOpen,
  Check,
  Package,
  Laptop,
  MapPin,
  Truck,
  Wrench,
  CalendarDays,
  Key,
  Video,
  ShoppingBag,
  Briefcase,
} from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { KbProfileFormValues } from '@/hooks/useKbProfileForm';
import { OFFERS, MODALITIES } from '@/hooks/useKbProfileForm';
import type { Offer, Modality } from '@/services/kbProfileService';
import { FieldHelp } from '@/components/onboarding/FieldHelp';

interface OfferModalityStepProps {
  form: UseFormReturn<KbProfileFormValues>;
}

const OFFER_ICONS: Record<Offer, React.ElementType> = {
  services: Briefcase,
  products_physical: Package,
  products_digital: Laptop,
};

const MODALITY_ICONS: Record<Modality, React.ElementType> = {
  presencial_fixo: MapPin,
  domicilio: Truck,
  online_live: Video,
  digital_on_demand: BookOpen,
  physical_shipping: ShoppingBag,
  made_to_order: Wrench,
  event_dated: CalendarDays,
  rental: Key,
};

interface SelectableCardProps {
  selected: boolean;
  onToggle: () => void;
  icon: React.ElementType;
  label: string;
  example?: string;
  testId?: string;
}

function SelectableCard({
  selected,
  onToggle,
  icon: Icon,
  label,
  example,
  testId,
}: SelectableCardProps) {
  // A11y note (F069 #7): the sr-only checkbox carries role + checked state
  // (multi-select ⇒ checkbox semantics; aria-pressed would be wrong here).
  // The visible Check badge + focus ring only mirror that state visually.
  return (
    <label
      data-testid={testId}
      className={[
        'relative flex flex-col items-center justify-center gap-1.5 min-h-[72px] px-3 py-3 rounded-xl border cursor-pointer transition-colors',
        'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2',
        selected ? 'border-foreground bg-muted font-medium' : 'border-border hover:bg-muted/50',
      ].join(' ')}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        className="sr-only"
        aria-label={label}
      />
      {selected && (
        <span
          data-testid={testId ? `${testId}-check` : undefined}
          className="absolute top-1.5 right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-foreground text-background"
          aria-hidden="true"
        >
          <Check className="h-3 w-3" />
        </span>
      )}
      <Icon className="h-5 w-5" aria-hidden="true" />
      <span className="text-xs text-center leading-snug">{label}</span>
      {example && (
        <span className="text-xs leading-tight text-center text-muted-foreground">{example}</span>
      )}
    </label>
  );
}

export function OfferModalityStep({ form }: OfferModalityStepProps) {
  const { t } = useTranslation();
  const selectedOffers = form.watch('offers');
  const selectedModalities = form.watch('modalities');

  const offersError = form.formState.errors.offers?.message;
  const modalitiesError = form.formState.errors.modalities?.message;

  function toggleOffer(offer: Offer) {
    const current = selectedOffers ?? [];
    form.setValue(
      'offers',
      current.includes(offer) ? current.filter((o) => o !== offer) : [...current, offer],
      { shouldValidate: true }
    );
  }

  function toggleModality(modality: Modality) {
    const current = selectedModalities ?? [];
    form.setValue(
      'modalities',
      current.includes(modality) ? current.filter((m) => m !== modality) : [...current, modality],
      { shouldValidate: true }
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="flex items-center gap-1">
          <p className="text-sm font-medium">{t('onboarding.kbForm.step1.offers_label')}</p>
          <FieldHelp baseKey="onboarding.kbForm.step1.offers" />
        </div>
        <p className="text-xs text-muted-foreground">{t('onboarding.kbForm.step1.offers_hint')}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-error-anchor="offers">
          {OFFERS.map((offer) => (
            <SelectableCard
              key={offer}
              selected={selectedOffers.includes(offer)}
              onToggle={() => toggleOffer(offer)}
              icon={OFFER_ICONS[offer]}
              label={t(`onboarding.kbForm.offer_${offer}`)}
              testId={`offer-card-${offer}`}
            />
          ))}
        </div>
        {offersError && (
          <p className="text-xs text-destructive" role="alert">
            {offersError}
          </p>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-1">
          <p className="text-sm font-medium">{t('onboarding.kbForm.step1.modalities_label')}</p>
          <FieldHelp baseKey="onboarding.kbForm.step1.modalities" />
        </div>
        <p className="text-xs text-muted-foreground">
          {t('onboarding.kbForm.step1.modalities_hint')}
        </p>
        <div
          className="grid grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-3 gap-2"
          data-error-anchor="modalities"
        >
          {MODALITIES.map((modality) => (
            <SelectableCard
              key={modality}
              selected={selectedModalities.includes(modality)}
              onToggle={() => toggleModality(modality)}
              icon={MODALITY_ICONS[modality]}
              label={t(`onboarding.kbForm.modality_${modality}`)}
              example={t(`onboarding.kbForm.modality_${modality}_example`)}
              testId={`modality-card-${modality}`}
            />
          ))}
        </div>
        {modalitiesError && (
          <p className="text-xs text-destructive" role="alert">
            {modalitiesError}
          </p>
        )}
      </div>
    </div>
  );
}
